import type { ChartOption } from "./chart-option.js"
import type { WidgetType } from "@csbi/contracts"
import type { ThemeMode } from "../palette.js"
import type { VizModel } from "../tile-model.js"
import { barLabelsOn } from "../display-support.js"
import { barOption, rankedBarOption } from "./bar.js"
import { gaugeOption } from "./gauge.js"
import { histogramOption } from "./histogram.js"
import { lineOption } from "./line.js"
import { mapOption, type MapView } from "./map.js"
import { pieOption } from "./pie.js"

export * from "./chart-option.js"
export * from "./base.js"
export * from "./bar.js"
export * from "./gauge.js"
export * from "./histogram.js"
export * from "./line.js"
export * from "./map.js"
export * from "./pie.js"

const CHART_WIDGETS = new Set<WidgetType>([
  "bar",
  "hbar",
  "stacked-bar",
  "line",
  "pie",
  "histogram",
  "gauge",
  "map",
])

export const isChartWidget = (widget: WidgetType): boolean => CHART_WIDGETS.has(widget)

export type ChartDisplay = {
  stacked?: boolean
  area?: boolean
  showDataLabels?: "none" | "endpoints" | "extremes" | "all"
  donut?: boolean
  legendPosition?: "bottom" | "right"
}

export type OptionContext = {
  knownRegions?: ReadonlySet<string>
  mapView?: MapView
}

export function optionFor(
  model: VizModel,
  mode: ThemeMode,
  display: ChartDisplay = {},
  ctx: OptionContext = {},
): ChartOption {
  const legendPosition = display.legendPosition

  switch (model.widget) {
    case "line":
      return lineOption({
        mode,
        model,
        area: display.area ?? false,
        dataLabels: display.showDataLabels ?? "endpoints",
        ...(legendPosition ? { legendPosition } : {}),
      })

    case "gauge":
      // Reaching here means the downgrade rules let it through: a single series, at most 8
      // categories, and a ratio unit. The maximum is therefore always 1 — other units never get
      // this far (see "a gauge must have a scale maximum" in downgrade.ts)
      return gaugeOption({ mode, model, max: 1 })

    case "map":
      return mapOption({
        mode,
        model,
        ...(ctx.knownRegions ? { known: ctx.knownRegions } : {}),
        ...(ctx.mapView ? { view: ctx.mapView } : {}),
      })

    case "histogram":
      /**
       * ⚠ **The histogram used to fall into the `rankedBarOption` branch below.** That draws
       * horizontal bars sorted by value — a **ranking** (which country set is largest), not a
       * **distribution** (how set sizes are spread). The two charts look alike and say entirely
       * different things, and drawing the wrong one does not error.
       */
      return histogramOption({
        mode,
        model,
        ...(display.showDataLabels ? { showDataLabels: barLabelsOn(display.showDataLabels) } : {}),
        ...(legendPosition ? { legendPosition } : {}),
      })

    case "hbar":
      return rankedBarOption({
        mode,
        model,
        stacked: false,
        ...(display.showDataLabels ? { showDataLabels: barLabelsOn(display.showDataLabels) } : {}),
        ...(legendPosition ? { legendPosition } : {}),
      })

    case "stacked-bar":
      return barOption({
        mode,
        model,
        stacked: true,
        ...(display.showDataLabels ? { showDataLabels: barLabelsOn(display.showDataLabels) } : {}),
        ...(legendPosition ? { legendPosition } : {}),
      })

    case "pie":
      return pieOption({
        mode,
        model,
        donut: display.donut ?? true,
        ...(legendPosition ? { legendPosition } : {}),
      })

    case "bar":
    default:
      return barOption({
        mode,
        model,
        stacked: display.stacked ?? false,
        ...(display.showDataLabels ? { showDataLabels: barLabelsOn(display.showDataLabels) } : {}),
        ...(legendPosition ? { legendPosition } : {}),
      })
  }
}
